/**
 * Wave-2 avatar pool — 51 Noto Animated Emojis + 30 curated cat photos.
 *
 * Each entry is a plain object with:
 *   - kind: "svg" | "emoji" | "cat"
 *   - url : same-origin path (bundled or copied into /public so the
 *           service-worker can pre-cache them)
 *   - tags: emotion / bucket labels the mood engine picks from
 *   - alt : accessible fallback for screen readers
 *   - bn  : Bangla-friendly name for the owner's reading pleasure
 *           (rendered in the UI only via title="", never as a11y name)
 *
 * The 8 base SVGs stay first (indices 0-7) so all existing hardcoded
 * MOOD_BASE / REACTION_BASE indices in ChargerIcon.jsx keep working
 * unchanged. New URL-based avatars stack after them.
 *
 * `poolByTag` at the bottom lets the mood engine ask e.g.
 *   pickByTag("scream")  → a random screaming emoji / cat / overheat SVG
 * without walking the whole list on every render.
 */

// ────────────────────────────────────────────────────────────────────
// Noto Animated Emoji entries (public/avatars/emoji/<code>.webp)
// Fetched by /app/scripts/fetch_noto_emojis.sh.
// 51 entries total; every one has been curl-verified 200 + image/webp.
// ────────────────────────────────────────────────────────────────────
const EMOJIS = [
  // Happy / smile family
  { code: "1f600", bn: "হাসিমুখ",        alt: "grinning face",           tags: ["happy","plus1","neutral"] },
  { code: "1f603", bn: "খুশি হাসি",       alt: "smiling face",            tags: ["happy","plus1","neutral"] },
  { code: "1f604", bn: "বড় হাসি",         alt: "big smile",               tags: ["happy","plus2","winner"] },
  { code: "1f601", bn: "উজ্জ্বল হাসি",     alt: "beaming face",            tags: ["happy","plus2","full"] },
  { code: "1f606", bn: "হো হো হাসি",       alt: "laughing hard",           tags: ["happy","plus3","winner"] },
  { code: "1f605", bn: "ঘামসহ হাসি",       alt: "sweat smile",             tags: ["charging","plus2","neutral"] },
  { code: "1f602", bn: "চোখে জল হাসি",     alt: "laughing tears",          tags: ["happy","plus3","winner","full"] },
  { code: "1f923", bn: "মাটিতে গড়াগড়ি",   alt: "rolling on floor",        tags: ["happy","plus4","overflow"] },
  { code: "1f60a", bn: "মিষ্টি হাসি",      alt: "blushing smile",          tags: ["happy","plus1","charging"] },
  // Love family
  { code: "1f60d", bn: "চোখে হার্ট",       alt: "heart eyes",              tags: ["love","plus2","full","winner"] },
  { code: "1f970", bn: "ভালবাসাসহ হাসি",   alt: "smiling with hearts",     tags: ["love","plus2","full"] },
  { code: "1f618", bn: "চুম্বন",           alt: "blowing kiss",            tags: ["love","plus1","neutral"] },
  { code: "1f617", bn: "চুমু",             alt: "kissing",                 tags: ["love","plus1"] },
  { code: "1f61a", bn: "বন্ধ চোখে চুমু",    alt: "closed-eye kiss",         tags: ["love","plus1"] },
  { code: "1f619", bn: "চুমুর হাসি",       alt: "kissing smile",           tags: ["love","plus1"] },
  { code: "1f972", bn: "কান্নাসহ হাসি",    alt: "tears of joy smile",      tags: ["happy","love","plus3"] },
  // Cool / party
  { code: "1f60e", bn: "কুল",              alt: "cool sunglasses",         tags: ["cool","plus3","winner"] },
  { code: "1f913", bn: "গবেষক",            alt: "nerd face",               tags: ["cool","neutral","charging"] },
  { code: "1f978", bn: "ছদ্মবেশ",          alt: "disguise",                tags: ["cool","neutral"] },
  { code: "1f929", bn: "তারা চোখ",         alt: "star-struck",             tags: ["winner","plus3","full"] },
  { code: "1f973", bn: "পার্টি",           alt: "party face",              tags: ["party","plus4","winner","full"] },
  { code: "1f389", bn: "উৎসব",             alt: "tada",                    tags: ["party","plus4","full"] },
  { code: "1f38a", bn: "কনফেটি",           alt: "confetti",                tags: ["party","plus4","full"] },
  { code: "1f947", bn: "স্বর্ণপদক",         alt: "gold medal",              tags: ["winner","plus3","full"] },
  { code: "1f3c6", bn: "ট্রফি",            alt: "trophy",                  tags: ["winner","plus3","full"] },
  { code: "1f4af", bn: "১০০ স্কোর",        alt: "hundred points",          tags: ["winner","plus5","full"] },
  // Fire / energy
  { code: "1f525", bn: "আগুন",             alt: "fire",                    tags: ["overflow","plus4","plus5","scream"] },
  { code: "26a1",  bn: "বিদ্যুৎ",          alt: "high voltage",            tags: ["charging","plus2","plus3"] },
  { code: "2728",  bn: "চকমক",             alt: "sparkles",                tags: ["winner","plus2","full"] },
  { code: "1f4ab", bn: "চক্করকাটা তারা",   alt: "dizzy star",              tags: ["loser","minus"] },
  { code: "1f4a5", bn: "বিস্ফোরণ",         alt: "collision boom",          tags: ["overflow","plus4","scream"] },
  { code: "1f4aa", bn: "মাসল",             alt: "flexed muscle",           tags: ["winner","plus3","full"] },
  { code: "1f680", bn: "রকেট",             alt: "rocket",                  tags: ["winner","plus5","overflow"] },
  { code: "1f3af", bn: "টার্গেট",          alt: "bullseye",                tags: ["target","full","plus3"] },
  { code: "1f4a3", bn: "বোমা",             alt: "bomb",                    tags: ["overflow","plus5","scream"] },
  // Sleep / tired
  { code: "1f634", bn: "ঘুম",              alt: "sleeping",                tags: ["sleep","neutral"] },
  { code: "1f62a", bn: "নিদ্রালু",         alt: "sleepy",                  tags: ["sleep","charging"] },
  { code: "1f971", bn: "হাই তোলা",         alt: "yawning",                 tags: ["sleep","neutral"] },
  // Dizzy / disgust
  { code: "1f635", bn: "চেতনাহীন",         alt: "knocked out",             tags: ["loser","minus","scream"] },
  { code: "1f974", bn: "মাথা ঘোরা",        alt: "woozy",                   tags: ["loser","minus"] },
  { code: "1f922", bn: "বমি ভাব",           alt: "nauseated",               tags: ["loser","minus"] },
  { code: "1f92e", bn: "বমি",              alt: "vomiting",                tags: ["loser","minus","scream"] },
  // Reactions
  { code: "1f92f", bn: "মাথা উড়ে",         alt: "mind blown",              tags: ["plus5","plus6","scream","overflow"] },
  { code: "1f976", bn: "শীত",              alt: "cold face",               tags: ["loser","minus","charging"] },
  { code: "1f975", bn: "গরম",              alt: "hot face",                tags: ["overflow","plus4","scream"] },
  { code: "1f914", bn: "ভাবছি",            alt: "thinking",                tags: ["neutral","charging"] },
  // Sad / angry
  { code: "1f622", bn: "কাঁদছে",           alt: "crying",                  tags: ["loser","minus"] },
  { code: "1f62d", bn: "জোরে কাঁদছে",       alt: "sobbing loudly",          tags: ["loser","minus","scream"] },
  { code: "1f621", bn: "রাগান্বিত",        alt: "pouting angry",           tags: ["overflow","scream","plus4"] },
  { code: "1f624", bn: "নাক থেকে ধোঁয়া",   alt: "steam from nose",         tags: ["overflow","scream","plus5"] },
  { code: "1f92c", bn: "গালাগাল",           alt: "cursing symbols",         tags: ["overflow","scream","plus5","plus6"] },
];

// Cat manifest is loaded at runtime from /avatars/cats/index.json —
// the fetch script writes ONLY successful downloads, so we don't have
// to hardcode any list here. If /public/avatars/cats/index.json is
// missing (dev environment where the fetch wasn't run) the cat pool
// silently stays empty.
export const CATS_MANIFEST_URL = "/avatars/cats/index.json";
// Emoji URLs live under /avatars/emoji.
const EMOJI_URL = (code) => `/avatars/emoji/${code}.webp`;

// ────────────────────────────────────────────────────────────────────
// Public pool. Consumed by ChargerIcon + useShuffledAvatars.
// ────────────────────────────────────────────────────────────────────
export const EMOJI_POOL = EMOJIS.map((e, i) => ({
  key: `em-${e.code}`,
  kind: "emoji",
  url: EMOJI_URL(e.code),
  tags: e.tags,
  bn: e.bn,
  alt: e.alt,
  code: e.code,
  poolIndex: i,
}));

/** All emoji entry URLs — used by the service-worker for precaching. */
export const EMOJI_URLS = EMOJI_POOL.map((e) => e.url);

/**
 * Fetch the cat manifest at runtime and normalise it into pool entries.
 * Returns a Promise so callers can await; safe to call multiple times
 * (results are cached on window for the tab lifetime). Never throws —
 * on failure returns an empty array so the app degrades gracefully to
 * the emoji + SVG pool.
 */
export async function loadCatPool() {
  if (typeof window === "undefined") return [];
  if (window.__refclickCatPool) return window.__refclickCatPool;
  try {
    const res = await fetch(CATS_MANIFEST_URL, { cache: "force-cache" });
    if (!res.ok) return [];
    const raw = await res.json();
    const list = (Array.isArray(raw) ? raw : []).map((c, i) => ({
      key: `cat-${c.id || i}`,
      kind: "cat",
      url: `/avatars/cats/${c.file}`,
      tags: c.tags || [],
      bn: "বিড়াল",
      alt: `Cat ${i + 1}`,
      code: c.id,
      poolIndex: i,
    }));
    window.__refclickCatPool = list;
    return list;
  } catch {
    window.__refclickCatPool = [];
    return [];
  }
}

/**
 * Map a mood/reaction key to the tags we'll search the pool for.
 * Keeping this as a plain object (rather than baking into pool entries)
 * lets us tweak the routing without re-tagging every asset.
 */
export const MOOD_TAG_MAP = {
  sleep:    ["sleep"],
  charging: ["charging"],
  neutral:  ["neutral","happy"],
  winner:   ["winner","full","happy"],
  full:     ["full","winner","happy"],
  overflow: ["overflow","scream"],
  loser:    ["loser","minus"],
  // Reaction tiers — six visibly different escalation buckets.
  plus1:    ["plus1","happy"],
  plus2:    ["plus2","happy"],
  plus3:    ["plus3","winner"],
  plus4:    ["plus4","scream"],
  plus5:    ["plus5","scream"],
  plus6:    ["plus6","scream","overflow"],
  target:   ["target","full","winner"],
  minus:    ["minus","loser"],
};

/**
 * Deterministically pick a pool entry whose tags intersect the mood.
 * `seed` is any positive integer (link-id hash + hour bucket, so each
 * link's choice is stable within an hour but visibly rotates hourly).
 *
 * Prefers emojis first (crisp animated), then cats, then falls through
 * to `null` (caller uses the base SVG). Every branch is bounded so an
 * empty pool never throws.
 */
export function pickPoolAvatar(mood, seed = 0, cats = []) {
  const tags = MOOD_TAG_MAP[mood] || MOOD_TAG_MAP.neutral;
  const emojis = EMOJI_POOL.filter((e) =>
    e.tags.some((t) => tags.includes(t)),
  );
  const relevantCats = (cats || []).filter((c) =>
    (c.tags || []).some((t) => tags.includes(t)),
  );
  const combined = emojis.concat(relevantCats);
  if (combined.length === 0) return null;
  const idx = Math.abs(seed) % combined.length;
  return combined[idx];
}
